import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { motion } from "framer-motion"

const TerminalCard = styled(motion.div)`
  font-family: "Courier New", "Courier", monospace;
  background: #111;
  border: 1px solid #333;
  padding: 0.75rem;
  border-radius: 6px;
  margin-top: 1.5rem;
  max-width: 400px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem;
  }

  .terminal-prompt {
    font-size: 0.7rem;
    color: #39ff14;
    margin-bottom: 0.4rem;
    font-weight: bold;
  }

  .header {
    font-size: 0.7rem;
    color: #666;
    border-bottom: 1px solid #333;
    padding-bottom: 0.4rem;
    margin-bottom: 0.6rem;
    display: flex;
    justify-content: space-between;
  }

  .content {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .album-art {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    flex-shrink: 0;
    background: #222;
  }

  .track-info {
    flex: 1;
    min-width: 0;
  }

  .track-name {
    color: #fff;
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0.2rem;
  }

  .process-line {
    color: #888;
    font-size: 0.75rem;
  }

  .currently-playing {
    color: #666;
    font-size: 0.65rem;
    font-style: italic;
    margin-bottom: 0.15rem;
  }

  .artist-name {
    color: #999;
    font-size: 0.75rem;
  }

  .status {
    color: #39ff14;
    font-weight: bold;
  }

  .idle-status {
    color: #666;
  }

  .spotify-icon {
    width: 14px;
    height: 14px;
    opacity: 0.5;
  }
`

const SpotifyNowPlaying = ({ userId }) => {
  const [track, setTrack] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        // Lanyard API endpoint
        const response = await fetch(
          `https://api.lanyard.rest/v1/users/${userId}`
        )

        if (!response.ok) {
          throw new Error("Failed to fetch")
        }

        const result = await response.json()

        // Check if user is currently listening
        if (
          result.success &&
          result.data.listening_to_spotify &&
          result.data.spotify
        ) {
          setTrack({
            name: result.data.spotify.song,
            artist: result.data.spotify.artist,
            albumArt: result.data.spotify.album_art_url,
            timestamp: result.data.spotify.timestamps?.start || Date.now(),
            isPlaying: true,
          })
        } else {
          // Show dummy track when not playing
          setTrack({
            name: "Buzzcut Season",
            artist: "Lorde",
            albumArt: "/lorde.png",
            timestamp: Date.now(),
            isPlaying: true,
          })
        }

        setLoading(false)
        setError(false)
      } catch (err) {
        console.error("Spotify fetch error:", err)
        setError(true)
        setLoading(false)
      }
    }

    fetchNowPlaying()

    // Refresh every 15 seconds for more real-time updates
    const interval = setInterval(fetchNowPlaying, 15000)

    return () => clearInterval(interval)
  }, [userId])

  if (loading) {
    return (
      <TerminalCard
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="header">
          <span>spotify.service</span>
          <span>...</span>
        </div>
        <div className="process-line idle-status">Loading...</div>
      </TerminalCard>
    )
  }

  if (error) {
    return null // Silently fail if API is unavailable
  }

  if (!track?.isPlaying) {
    return (
      <TerminalCard
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="header">
          <span>spotify.service</span>
          <span>inactive</span>
        </div>
        <div className="process-line idle-status">No active processes</div>
      </TerminalCard>
    )
  }

  const timestamp = track.timestamp || Date.now()
  const pid = String(timestamp).slice(-4)

  return (
    <TerminalCard
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <div className="terminal-prompt">$ spotify --now-playing</div>
      <div className="header">
        <span>PID: {pid}</span>
        <span>MEM: 128MB</span>
      </div>
      <div className="content">
        {track.albumArt && (
          <img
            src={track.albumArt}
            alt={`${track.name} album art`}
            className="album-art"
          />
        )}
        <div className="track-info">
          <div className="track-name">{track.name}</div>
          <div className="artist-name">{track.artist}</div>
        </div>
      </div>
    </TerminalCard>
  )
}

SpotifyNowPlaying.propTypes = {
  userId: PropTypes.string.isRequired,
}

export default SpotifyNowPlaying
